"use client";

import React, { useEffect, useRef } from 'react';

interface ContactEntry {
  label: string;
  email: string;
  description: string;
}

const CONTACTS: ContactEntry[] = [
  {
    label: 'General enquiries',
    email: 'info@pelagicrobotics.io',
    description: 'Platform capabilities, technical specifications, and commercial operations.',
  },
  {
    label: 'Investor enquiries',
    email: 'investors@pelagicrobotics.io',
    description: 'Investment opportunities and company overview.',
  },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 130);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="section-padding relative overflow-hidden"
      aria-labelledby="contact-heading"
    >
      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px]"
        aria-hidden="true"
        style={{
          background: 'radial-gradient(ellipse, rgba(0,102,204,0.12) 0%, rgba(0,212,200,0.04) 40%, transparent 70%)',
          filter: 'blur(80px)',
        }}
      />

      <div className="max-w-7xl mx-auto px-5 lg:px-12 relative z-10">
        {/* Teal divider */}
        <div className="teal-line mb-10 md:mb-16 reveal-hidden" />

        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 mb-8 md:mb-14">
          <div className="lg:col-span-5 reveal-hidden">
            <div className="section-label mb-4">02 / Contact</div>
            <h2
              id="contact-heading"
              className="text-seafoam"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                fontWeight: 700,
                lineHeight: 1.0,
                letterSpacing: '-0.03em',
              }}
            >
              Contact
            </h2>
          </div>
          <div className="lg:col-span-7 flex items-end reveal-hidden">
            <p
              style={{
                fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
                color: 'rgba(232, 237, 244, 0.85)',
                fontWeight: 300,
                lineHeight: 1.8,
                maxWidth: '480px',
              }}
            >
              Reach out to discuss survey requirements, platform capabilities, or commercial partnerships.
            </p>
          </div>
        </div>

        {/* Contact cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CONTACTS.map((contact, i) => (
            <ContactCard key={contact.email} contact={contact} delay={i * 120} />
          ))}
        </div>

        {/* Bottom teal line */}
        <div className="teal-line mt-10 md:mt-16 reveal-hidden" />
      </div>
    </section>
  );
}

function ContactCard({ contact, delay }: { contact: ContactEntry; delay: number }) {
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={cardRef}
      className="contact-card reveal-hidden rounded-none p-6 md:p-8 flex flex-col gap-4 md:gap-5"
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Label */}
      <div className="section-label" style={{ opacity: 0.7 }}>
        {contact.label}
      </div>

      {/* Teal line */}
      <div
        style={{
          width: '24px',
          height: '1px',
          background: 'var(--teal-glow)',
          opacity: 0.4,
        }}
      />

      {/* Description */}
      <p
        style={{
          fontSize: 'clamp(1rem, 1.5vw, 1.2rem)',
          color: 'rgba(232, 237, 244, 0.85)',
          fontWeight: 300,
          lineHeight: 1.8,
          flexGrow: 1,
        }}
      >
        {contact.description}
      </p>

      {/* Email */}
      <a
        href={`mailto:${contact.email}`}
        className="email-link group flex items-center gap-3"
        aria-label={`Send email to ${contact.email}`}
      >
        <span style={{ fontSize: '0.78rem', letterSpacing: '0.04em' }}>
          {contact.email}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-transform duration-300 group-hover:translate-x-1"
          aria-hidden="true"
        >
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </a>
    </div>
  );
}